import React, { useEffect, useMemo, useState } from "react";
import { History, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/common";
import { CustomerTimeline } from "@/components/customer/CustomerTimeline";

const FILTERS = [
  ["alle", "Alt", () => true],
  ["endringer", "Endringer", (e) => ["edit", "undo", "items", "approved", "proposal", "rejected"].includes(e.type)],
  ["dokumenter", "Dokumenter", (e) => e.type === "document" || e.type === "reading"],
  ["tilbud", "Tilbud", (e) => ["quote", "controlled", "text", "risk"].includes(e.type)],
  ["notater", "Notater", (e) => e.type === "note" || e.type === "decision"],
  ["sak", "Saken", (e) => ["draft", "request", "workspace", "discarded", "restored"].includes(e.type)],
];

/** Everything that happened on this case – who, what, when – merged from edits, documents, KI, texts, revisions and notes. */
export function ActivityPanel({ open, onOpenChange, projectId, onGoto }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("alle");
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { setData((await api.get(`/projects/${projectId}/activity`)).data); } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) load(); }, [open, projectId]);
  const events = useMemo(() => {
    const f = FILTERS.find((x) => x[0] === filter) || FILTERS[0];
    return (data?.events || []).filter(f[2]);
  }, [data, filter]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map(([k, , fn]) => [k, (data?.events || []).filter(fn).length])), [data]);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" aria-describedby={undefined} className="flex w-full flex-col gap-0 overflow-hidden border-l-black/[0.06] p-0 sm:max-w-[520px]" data-testid="activity-panel">
        <SheetHeader className="shrink-0 space-y-1 px-6 pb-3 pt-6 text-left">
          <SheetTitle className="flex items-center gap-2 font-display text-[20px] tracking-[-0.02em]"><History className="h-[18px] w-[18px] text-muted-foreground" /> Aktivitet på saken</SheetTitle>
          <SheetDescription className="text-[12.5px]">Hvem gjorde hva, når – endringer i kalkylen, dokumenter, KI-forslag, tekster, revisjoner og notater. Lesende; ingenting endres her.</SheetDescription>
        </SheetHeader>
        <div className="flex shrink-0 flex-wrap items-center gap-2 px-6 pb-3">
          <div className="inline-flex max-w-full flex-wrap rounded-full bg-black/[0.045] p-1" role="tablist" aria-label="Filter" data-testid="activity-filter">
            {FILTERS.filter(([k]) => k === "alle" || counts[k]).map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={filter === k} onClick={() => setFilter(k)} className={cn("flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[12.5px] text-muted-foreground transition-[color,background-color,box-shadow] hover:text-foreground", filter === k && "bg-white font-medium text-foreground shadow-[0_1px_2px_hsl(212_40%_16%/0.08),0_0_0_1px_hsl(212_40%_16%/0.05)]")} data-testid={`activity-filter-${k}`}>
                {label} <span className="font-mono text-[11px] tabular-nums opacity-70">{counts[k] ?? 0}</span>
              </button>
            ))}
          </div>
          <button type="button" onClick={load} disabled={loading} className="ml-auto inline-flex h-7 items-center gap-1 rounded-full px-2 text-[12px] text-muted-foreground hover:bg-black/[0.05] hover:text-foreground disabled:opacity-40" data-testid="activity-refresh"><RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} /> Oppdater</button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-8 scrollbar-thin">
          {!data && loading ? <div className="space-y-3 pt-2"><Skeleton className="h-10" /><Skeleton className="h-10" /><Skeleton className="h-10" /></div>
            : !events.length ? <p className="py-8 text-center text-[13px] text-muted-foreground" data-testid="activity-empty">Ingen hendelser i dette utvalget ennå.</p>
              : <CustomerTimeline events={events} initial={40} onPick={onGoto ? (e) => { if (e.tab) { onGoto(e.tab, e.ref); onOpenChange(false); } } : undefined} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}
